import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import BottomNav from './pages/component/BottomNav';
import Header from './pages/component/Header';
import Wallet from './pages/component/Wallet';
import { UserProvider } from './pages/component/UserContext';
import { ProfileProvider } from './pages/component/ProfileContext'; // Import the provider and the custom hook
import { ModalProvider } from './pages/component/ModalContext';
import { AstroAvatarsProvider } from './pages/component/AstroAvatarsContext';
import { RechargeOptionsProvider } from './pages/component/RechargeOptionsContext';
import TermCondition from './pages/component/TermCondition';
import SplashScreen from './pages/component/SplashScreen';
import GoogleMapsLoader from './pages/component/GoogleMapsLoader';
import ErrorBoundary from './pages/component/ErrorBoundary';
import { ROUTES } from './pages/component/routes';
import useBackButtonHandler from './pages/component/useBackButtonHandler';
import { trackEvent } from './pages/component/trackingService';
import { PushNotifications } from '@capacitor/push-notifications';
import AddProfile from './pages/component/AddProfile';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';


const Auth = lazy(() => import('./pages/Auth'));
const Chat = lazy(() => import('./pages/Chat'));
const ChatSessions = lazy(() => import('./pages/ChatSessions'));
const Account = lazy(() => import('./pages/Account'));
const Starlight = lazy(() => import('./pages/Starlight'));
const Home = lazy(() => import('./pages/Home'));

const AppContent = ({ isLoggedIn, setIsLoggedIn }) => {
  const [walletOpen, setWalletOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setUser(user);
    if (user) {
      trackEvent('login');
    }
  }, [isLoggedIn]);

  useBackButtonHandler(location, navigate);



  const ProtectedRoute = ({ children }) => {
    return isLoggedIn ? children : <Navigate to={ROUTES.LOGIN} />;
  };

  const hideHeaderFooter = location.pathname === ROUTES.LOGIN || location.pathname.startsWith('/chat/');

  const handleWalletOpen = () => {
    setWalletOpen(true);
    trackEvent('wallet_open');
  };

  const handleWalletClose = () => {
    setWalletOpen(false);
    trackEvent('wallet_close');
  };

  const [termConditionOpen, setTermConditionOpen] = useState(false);

  const handleTermConditionOpen = () => {
    setTermConditionOpen(true);
    trackEvent('term_condition_open');
  };

  const handleTermConditionClose = () => {
    setTermConditionOpen(false);
    trackEvent('term_condition_close');
  };

  return (
    <div className="app-container">
      {!hideHeaderFooter && (
        <div className="header">
          <Header onWalletOpen={handleWalletOpen} user={user} onTermConditionOpen={handleTermConditionOpen} />
        </div>
      )}
      <div className={hideHeaderFooter ? "" : "content"}>
        <Routes>
          <Route path={ROUTES.LOGIN} element={<Auth setIsLoggedIn={setIsLoggedIn} />} />
          <Route path={ROUTES.HOME} element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path={ROUTES.CHAT_SESSIONS} element={<ProtectedRoute><ChatSessions /></ProtectedRoute>} />
          <Route path={ROUTES.STARLIGHT} element={<ProtectedRoute><Starlight /></ProtectedRoute>} />
          <Route path={`${ROUTES.CHAT}/:sessionId`} element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path={ROUTES.ACCOUNT} element={<ProtectedRoute><Account /></ProtectedRoute>} />
        </Routes>
      </div>
      {!hideHeaderFooter && (
        <div className="footer">
          <BottomNav />
        </div>
      )}
      <Wallet show={walletOpen} handleClose={handleWalletClose} />
      <TermCondition show={termConditionOpen} handleClose={handleTermConditionClose} />
      <AddProfile />
    </div>
  );
};

const AppWrapper = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));

    if (token && user) {
      setIsLoggedIn(true);
    }
    setIsAuthChecked(true);

    const splashTimeout = setTimeout(() => {
      setShowSplash(false);
    }, 2000); // 2 seconds

    return () => clearTimeout(splashTimeout);

  }, []);



  if (!isAuthChecked || showSplash) {
    return <SplashScreen />;
  }

  return (
    <Router>
      <GoogleMapsLoader apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} loadingComponent={<SplashScreen />}>
        <ErrorBoundary>
          {isLoggedIn ? (
            <UserProvider>
              <ProfileProvider>
                <ModalProvider>
                  <AstroAvatarsProvider>
                    <RechargeOptionsProvider>
                        <Suspense fallback={<SplashScreen />}>
                          <AppContent isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
                        </Suspense>    
                    </RechargeOptionsProvider>
                  </AstroAvatarsProvider>
                </ModalProvider>
              </ProfileProvider>
            </UserProvider>
          ) : (
            <Suspense fallback={<SplashScreen />}>
              <Routes>
                <Route path={ROUTES.LOGIN} element={<Auth setIsLoggedIn={setIsLoggedIn} />} />
                <Route path="*" element={<Navigate to={ROUTES.LOGIN} />} />
              </Routes>
            </Suspense>
          )}
        </ErrorBoundary>
      </GoogleMapsLoader>
    </Router>
  );
};

export default AppWrapper;
